/**
 * FullVSL custom HTML5 player — prototype slice (FUL-7).
 *
 * Scope (per directive): direct-MP4 playback, aspect-ratio auto-detection,
 * basic custom controls (play/pause, seek, mute/unmute, fullscreen),
 * responsive container sizing for the detected ratio, native controls disabled.
 *
 * FUL-8 extension: multi-aspect-ratio handling now also drives the overlay/CTA
 * safe-zone grid (see ./fullvsl-safe-zones). The grid renders the 7 position
 * slots the Phase 0 contract defines, positioned inside the safe area for the
 * resolved ratio, so later overlay/CTA work targets the exact layout regions.
 *
 * FUL-9 extension: watched-progress delayed CTA reveal + delayed page elements
 * by selector (see ./fullvsl-cta). The player tracks `maxWatched` (greatest
 * contiguous watched position) and feeds a `CtaController`, which reveals the
 * CTA at a configured threshold. It also emits normalized `PlayerEvent`s on the
 * FUL-5 vocabulary (`progress`, `milestone`, `cta_shown`, `cta_clicked`, …)
 * through the optional `onEvent` dispatcher — a no-op stub by default since the
 * FUL-6 analytics schema is still in-flight.
 */

import type {
  AutoplayConfig,
  CtaConfig,
  DelayedElement,
  Overlay,
  PlayerEvent,
  PlayerEventDispatcher,
  ProgressBarConfig,
  ResumeConfig,
} from "../../contracts/src/contracts";
import {
  CtaController,
  advanceMaxWatched,
  type WatchProgress,
} from "./fullvsl-cta";
import {
  OverlayController,
  type OverlayActionHandlers,
} from "./fullvsl-overlay";
import {
  OVERLAY_POSITIONS,
  resolveSafeZones,
  resolveSafeZonesByOrientation,
  type SafeZoneLayout,
  type SafeZoneRegion,
} from "./fullvsl-safe-zones";
import { EmbedBridge } from "./embed-bridge";
import { DEFAULT_RESUME_CONFIG, ResumeWiring } from "./resume-wiring";
import {
  fromVisualProgress,
  normalizeProgressBarConfig,
  toVisualProgress,
} from "./progress-curve";

/**
 * Milestone percents the player emits exactly once each, based on `maxWatched`
 * (mirrors `MILESTONES` from @fullvsl/contracts; kept local so the player bundle
 * stays dependency-free).
 */
const MILESTONES = [25, 50, 75, 100] as const;
/**
 * Progress-event throttle (mirrors `PROGRESS_THROTTLE` from @fullvsl/contracts):
 * emit at most every 1s AND every 5% of duration, whichever is later.
 */
const PROGRESS_MIN_INTERVAL_MS = 1000;
const PROGRESS_MIN_PCT_STEP = 5;

export type StandardAspectRatio = "16:9" | "9:16" | "1:1" | "4:5" | "3:4";
export type AspectRatioInput = StandardAspectRatio | "auto" | "custom";

/** Coarse orientation derived from the width/height ratio. */
export type Orientation = "landscape" | "portrait" | "square";

export interface DetectedAspect {
  /** Human label, "custom" when no standard ratio is close enough. */
  label: StandardAspectRatio | "custom";
  width: number;
  height: number;
  /** width / height */
  ratio: number;
  /** Coarse orientation bucket (landscape / portrait / square). */
  orientation: Orientation;
}

/** Thresholds used to bucket a ratio into an orientation. */
export const LANDSCAPE_MIN = 1.05;
export const PORTRAIT_MAX = 0.95;

/** Resolve a width/height ratio into a coarse orientation. */
export function resolveOrientation(width: number, height: number): Orientation {
  const safeH = height > 0 ? height : 1;
  const ratio = width / safeH;
  if (ratio > LANDSCAPE_MIN) return "landscape";
  if (ratio < PORTRAIT_MAX) return "portrait";
  return "square";
}

export interface FullVslPlayerOptions {
  /** Direct MP4 (or otherwise HTML5-playable) source URL. */
  sourceUrl: string;
  /** Host element the player mounts into. */
  container: HTMLElement;
  /**
   * How the frame is sized. "auto" (default) detects from media metadata,
   * falling back to 16:9 until detection completes or if it fails.
   */
  aspectRatio?: AspectRatioInput;
  /**
   * Explicit custom ratio (Phase 0 contract: `VslProject.customRatio {w,h}`).
   * Used only when `aspectRatio === "custom"`; takes priority over detected
   * media dimensions so an author can pin a frame shape.
   */
  customRatio?: { w: number; h: number };
  /** Start muted. Defaults to false. */
  muted?: boolean;
  /** Loop playback. Defaults to false. */
  loop?: boolean;
  /** Optional callback fired when the aspect ratio is resolved/detected. */
  onAspectDetected?: (aspect: DetectedAspect) => void;
  /** Optional callback fired whenever the frame orientation is (re)applied. */
  onOrientationChange?: (orientation: Orientation) => void;
  /**
   * Render the overlay/CTA safe-zone grid (editor/debug aid). The grid is the
   * layout contract later overlay/CTA work targets. Defaults to false.
   */
  showSafeZones?: boolean;
  /**
   * FUL-9: watched-progress delayed CTA config. When set, the player reveals a
   * CTA overlay once watched progress (`maxWatched` by default) crosses the
   * configured threshold. See CtaConfig / ./fullvsl-cta.
   */
  cta?: CtaConfig;
  /**
   * FUL-9: page elements (by CSS selector) to reveal on a watched-progress or
   * event trigger (ctaClick / pause / complete / timestamp / percent).
   */
  delayedElements?: DelayedElement[];
  /**
   * FUL-11: editable overlay templates to render at their moments (start,
   * autoplay, minihook, pause, cta_reveal, end). See ./fullvsl-overlay.
   */
  overlays?: Overlay[];
  /** Optional CTA copy overrides. */
  ctaHeadline?: string;
  ctaSubtext?: string;
  ctaButtonLabel?: string;
  /**
   * FUL-9: normalized player-event dispatcher (FUL-5 vocabulary). The player
   * emits `progress`, `milestone`, `cta_shown`, `cta_clicked`, `play`, `pause`,
   * `completed` through this. Defaults to a no-op stub (FUL-6 schema in-flight).
   */
  onEvent?: PlayerEventDispatcher;
  /** Identity used in emitted events. Default to a generated/demo identity. */
  vslId?: string;
  sessionId?: string;
  visitorId?: string;
  /**
   * FUL-107 (IFRAME-8): smart autoplay. When set, the player attempts muted
   * autoplay on load (with a "tap for sound" prompt via the autoplay overlay).
   * If unmuted autoplay is configured and blocked, it falls back to muted.
   */
  autoplay?: AutoplayConfig;
  /** Visual-only accelerated progress curve. Media time and analytics remain real. */
  progressBar?: ProgressBarConfig;
  /** Per-VSL player accent color. */
  brandColor?: string;
  /** Whether normalized events are posted to the embedding parent window. */
  emitAnalyticsToParent?: boolean;
  /**
   * FUL-107 (IFRAME-4): resume-play. When set, the player persists progress
   * client-side (LocalStorage → cookie → memory) and offers to resume on a
   * return visit (mode "ask") or resumes silently ("always_resume").
   */
  resume?: ResumeConfig;
  /** Funnel/page scope used by the resume composite key when a FF context exists. */
  funnelId?: string;
  pageId?: string;
  /**
   * FUL-107 (IFRAME-7): postMessage bridge to the embedding host. Defaults to
   * active when embedded (window.parent !== window). Pass an allow-list to pin
   * the targetOrigin for strict origin hygiene.
   */
  allowedParentOrigins?: string[];
}

/** Candidate standard ratios with their exact w/h values. */
const STANDARD_RATIOS: ReadonlyArray<{ label: StandardAspectRatio; ratio: number }> = [
  { label: "16:9", ratio: 16 / 9 },
  { label: "9:16", ratio: 9 / 16 },
  { label: "1:1", ratio: 1 },
  { label: "4:5", ratio: 4 / 5 },
  { label: "3:4", ratio: 3 / 4 },
];

/** Match window: |detected - standard| / standard <= TOLERANCE. */
const RATIO_TOLERANCE = 0.02;

/** Classify raw pixel dimensions into a labeled aspect ratio. */
export function classifyAspectRatio(width: number, height: number): DetectedAspect {
  const safeH = height > 0 ? height : 1;
  const ratio = width / safeH;
  let best = STANDARD_RATIOS[0];
  let bestDiff = Infinity;
  for (const cand of STANDARD_RATIOS) {
    const diff = Math.abs(ratio - cand.ratio);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = cand;
    }
  }
  const withinTolerance = bestDiff <= RATIO_TOLERANCE * best.ratio;
  return {
    label: withinTolerance ? best.label : "custom",
    width,
    height,
    ratio,
    orientation: resolveOrientation(width, height),
  };
}

const CSS = `
.fvsl-frame{position:relative;width:100%;max-width:960px;margin:0 auto;background:#000;overflow:hidden}
.fvsl-frame[data-orientation="portrait"]{max-width:min(420px,100%)}
.fvsl-frame[data-orientation="square"]{max-width:min(720px,100%)}
.fvsl-frame[data-orientation="landscape"]{max-width:960px}
.fvsl-media{display:block;width:100%;height:100%;object-fit:contain;background:#000}
.fvsl-ctrl{position:absolute;left:0;right:0;bottom:0;padding:10px 12px;display:flex;align-items:center;gap:10px;background:linear-gradient(0deg,rgba(0,0,0,.72),rgba(0,0,0,0));color:#fff;font:13px/1.3 system-ui,-apple-system,Segoe UI,Roboto,sans-serif;opacity:0;transition:opacity .2s ease;user-select:none}
.fvsl-frame:hover .fvsl-ctrl,.fvsl-ctrl.is-visible,.fvsl-frame.fvsl-paused .fvsl-ctrl{opacity:1}
.fvsl-btn{appearance:none;border:0;background:rgba(255,255,255,.12);color:#fff;border-radius:8px;width:34px;height:34px;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;flex:0 0 auto}
.fvsl-btn:hover{background:rgba(255,255,255,.22)}
.fvsl-btn svg{width:18px;height:18px;display:block}
.fvsl-seek{position:relative;flex:1 1 auto;height:18px;display:flex;align-items:center;cursor:pointer;touch-action:none}
.fvsl-seek-track{position:relative;width:100%;height:5px;border-radius:3px;background:rgba(255,255,255,.28);overflow:hidden}
.fvsl-seek-buffered{position:absolute;left:0;top:0;bottom:0;background:rgba(255,255,255,.42)}
.fvsl-seek-played{position:absolute;left:0;top:0;bottom:0;background:var(--fvsl-brand,#00685f)}
.fvsl-time{white-space:nowrap;opacity:.9;min-width:96px;text-align:right;font-variant-numeric:tabular-nums}
.fvsl-center{position:absolute;inset:0;display:flex;align-items:center;justify-content:center}
.fvsl-bigplay{width:74px;height:74px;border-radius:50%;border:0;background:rgba(0,0,0,.55);color:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:transform .12s ease,background .12s ease}
.fvsl-bigplay:hover{background:rgba(0,0,0,.7);transform:scale(1.05)}
.fvsl-bigplay svg{width:32px;height:32px;margin-left:3px}
.fvsl-frame.fvsl-playing .fvsl-bigplay{display:none}
.fvsl-frame:not(.fvsl-playing) .fvsl-ctrl{opacity:1}
.fvsl-frame.fvsl-fullscreen{max-width:none;width:100%;height:100%}
.fvsl-safezones{position:absolute;inset:0;pointer-events:none;z-index:2}
.fvsl-safezones:not(.is-on){display:none}
.fvsl-cta-layer{position:absolute;inset:0;z-index:3;pointer-events:none}
.fvsl-overlay-layer{position:absolute;inset:0;z-index:4;pointer-events:none}
.fvsl-resume-layer{position:absolute;inset:0;z-index:5;pointer-events:none;display:flex;align-items:flex-start;justify-content:center;padding-top:14%}
.fvsl-resume{pointer-events:auto;display:flex;align-items:center;gap:10px;flex-wrap:wrap;justify-content:center;max-width:90%;background:rgba(8,12,16,.86);border:1px solid rgba(107,216,203,.35);border-radius:10px;padding:10px 14px;color:#fff;font:13px/1.35 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;box-shadow:0 12px 30px rgba(0,0,0,.45);opacity:0;transform:translateY(-6px);transition:opacity .2s ease,transform .2s ease;backdrop-filter:blur(6px)}
.fvsl-resume.is-visible{opacity:1;transform:translateY(0)}
.fvsl-resume-text{font-weight:600}
.fvsl-resume-btn{appearance:none;border:0;border-radius:8px;background:var(--fvsl-brand,#00685f);color:#fff;font:600 13px/1 system-ui,-apple-system,sans-serif;padding:9px 14px;cursor:pointer;transition:filter .12s ease}
.fvsl-resume-btn:hover{filter:brightness(1.12)}
.fvsl-resume-btn--ghost{background:rgba(255,255,255,.16)}
.fvsl-resume-btn--ghost:hover{background:rgba(255,255,255,.26)}
.fvsl-toast{position:absolute;left:50%;bottom:56px;transform:translate(-50%,8px);z-index:6;background:rgba(8,12,16,.88);color:#fff;font:12px/1.4 system-ui,-apple-system,sans-serif;padding:8px 12px;border-radius:8px;opacity:0;transition:opacity .2s ease,transform .2s ease;pointer-events:none;max-width:88%;text-align:center;border:1px solid rgba(255,255,255,.14)}
.fvsl-safezone{position:absolute;border:1px dashed rgba(107,216,203,.75);background:rgba(0,104,95,.10);border-radius:4px;display:flex;align-items:flex-start;justify-content:flex-start;padding:4px}
.fvsl-safezone-tag{font:600 10px/1 Inter,system-ui,-apple-system,sans-serif;letter-spacing:.05em;text-transform:uppercase;color:#6bd8cb;background:rgba(0,0,0,.5);padding:2px 5px;border-radius:3px;white-space:nowrap}
.fvsl-toast.is-visible{opacity:1;transform:translate(-50%,0)}
@media (max-width:640px){.fvsl-frame[data-orientation="landscape"]{max-width:100%}.fvsl-time{min-width:84px}}
`;

let cssInjected = false;
export function injectPlayerCss(): void {
  if (cssInjected) return;
  cssInjected = true;
  const style = document.createElement("style");
  style.setAttribute("data-fullvsl-player", "");
  style.textContent = CSS;
  document.head.appendChild(style);
}

export const ICON_PLAY =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
export const ICON_PAUSE =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>';
export const ICON_MUTED =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M4 9v6h4l5 5V4L8 9H4zm12.5 3a3.5 3.5 0 0 0-1.9-3.1v6.2A3.5 3.5 0 0 0 16.5 12zM14 4.4v2.05A5.5 5.5 0 0 1 14 17.6v2.05A7.5 7.5 0 0 0 14 4.4z" opacity=".4"/><path d="M16 9l5 5m0-5l-5 5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>';
export const ICON_VOL =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M4 9v6h4l5 5V4L8 9H4zm12.5 3a3.5 3.5 0 0 0-1.9-3.1v6.2A3.5 3.5 0 0 0 16.5 12zM14 4.4v2.05A5.5 5.5 0 0 1 14 17.6v2.05A7.5 7.5 0 0 0 14 4.4z"/></svg>';
export const ICON_FS =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M5 5h5v2H7v3H5V5zm9 0h5v5h-2V7h-3V5zM5 14h2v3h3v2H5v-5zm12 0h2v5h-5v-2h3v-3z"/></svg>';

export function fmtTime(s: number): string {
  if (!Number.isFinite(s) || s < 0) s = 0;
  const total = Math.floor(s);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  const ss = String(sec).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Generate a short random id (no crypto dependency required for the prototype). */
export function generateId(prefix: string): string {
  const rnd = Math.random().toString(36).slice(2, 10);
  const time = Date.now().toString(36).slice(-4);
  return `${prefix}_${time}${rnd}`;
}

const VISITOR_STORAGE_KEY = "fullvsl:visitor";

/** Stable visitor id persisted in localStorage (cookie fallback omitted in MVP). */
export function getOrCreateVisitorId(): string {
  try {
    const stored = localStorage.getItem(VISITOR_STORAGE_KEY);
    if (stored) return stored;
    const id = generateId("visitor");
    localStorage.setItem(VISITOR_STORAGE_KEY, id);
    return id;
  } catch {
    return generateId("visitor");
  }
}

/**
 * Embeddable custom HTML5 player. Framework-light: pure DOM, no runtime deps.
 */
export class FullVslPlayer {
  private readonly opts: Required<
    Omit<
      FullVslPlayerOptions,
      | "onAspectDetected"
      | "onOrientationChange"
      | "customRatio"
      | "cta"
      | "delayedElements"
      | "overlays"
      | "ctaHeadline"
      | "ctaSubtext"
      | "ctaButtonLabel"
      | "onEvent"
      | "vslId"
      | "sessionId"
      | "visitorId"
      | "autoplay"
      | "brandColor"
      | "emitAnalyticsToParent"
      | "resume"
      | "funnelId"
      | "pageId"
      | "allowedParentOrigins"
    >
  > & {
    onAspectDetected?: (aspect: DetectedAspect) => void;
    onOrientationChange?: (orientation: Orientation) => void;
    customRatio?: { w: number; h: number };
    cta?: CtaConfig;
    delayedElements?: DelayedElement[];
    overlays?: Overlay[];
    ctaHeadline?: string;
    ctaSubtext?: string;
    ctaButtonLabel?: string;
    onEvent?: PlayerEventDispatcher;
    vslId?: string;
    sessionId?: string;
    visitorId?: string;
    autoplay?: AutoplayConfig;
    brandColor?: string;
    emitAnalyticsToParent?: boolean;
    resume?: ResumeConfig;
    funnelId?: string;
    pageId?: string;
    allowedParentOrigins?: string[];
  };
  private readonly frame: HTMLElement;
  private readonly video: HTMLVideoElement;
  private readonly played: HTMLElement;
  private readonly buffered: HTMLElement;
  private readonly timeLabel: HTMLElement;
  private readonly playBtn: HTMLButtonElement;
  private readonly muteBtn: HTMLButtonElement;
  private readonly fsBtn: HTMLButtonElement;
  private readonly bigPlay: HTMLButtonElement;
  private readonly seek: HTMLElement;
  private readonly safeZoneLayer: HTMLElement;
  private readonly ctaLayer: HTMLElement;
  private readonly overlayLayer: HTMLElement;
  private readonly resumeLayer: HTMLElement;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private lastAppliedOrientation: Orientation = "landscape";
  private detectedAspectField: DetectedAspect | null = null;
  /** FUL-9: watched-progress tracking + event emission. */
  private ctaController: CtaController | null = null;
  /** FUL-11: overlay moment renderer. */
  private overlayController: OverlayController | null = null;
  /** FUL-107 (IFRAME-7): postMessage bridge to the embedding host. */
  private readonly embedBridge: EmbedBridge;
  /** FUL-107 (IFRAME-4): resume-play persistence controller. */
  private resumeWiring: ResumeWiring | null = null;
  /** FUL-107 (IFRAME-8): whether muted autoplay has engaged. */
  private autoplayEngaged = false;
  /** FUL-107 (IFRAME-9): cached fullscreen capability probe. */
  private fullscreenBlocked = false;
  private maxWatchedSec = 0;
  private lastCurrentTimeSec = 0;
  private eventSeq = 0;
  private lastProgressEmitMs = 0;
  private lastProgressEmitPct = -1;
  private readonly milestonesHit = new Set<number>();
  private readonly sessionId: string;
  private readonly visitorId: string;
  private readonly vslId: string;

  constructor(opts: FullVslPlayerOptions) {
    injectPlayerCss();
    this.opts = {
      aspectRatio: opts.aspectRatio ?? "auto",
      muted: opts.muted ?? false,
      loop: opts.loop ?? false,
      showSafeZones: opts.showSafeZones ?? false,
      sourceUrl: opts.sourceUrl,
      container: opts.container,
      customRatio: opts.customRatio,
      onAspectDetected: opts.onAspectDetected,
      onOrientationChange: opts.onOrientationChange,
      cta: opts.cta,
      delayedElements: opts.delayedElements,
      overlays: opts.overlays,
      ctaHeadline: opts.ctaHeadline,
      ctaSubtext: opts.ctaSubtext,
      ctaButtonLabel: opts.ctaButtonLabel,
      onEvent: opts.onEvent,
      vslId: opts.vslId,
      sessionId: opts.sessionId,
      visitorId: opts.visitorId,
      autoplay: opts.autoplay,
      progressBar: normalizeProgressBarConfig(opts.progressBar),
      brandColor: opts.brandColor,
      emitAnalyticsToParent: opts.emitAnalyticsToParent ?? true,
      resume: opts.resume,
      funnelId: opts.funnelId,
      pageId: opts.pageId,
      allowedParentOrigins: opts.allowedParentOrigins,
    };

    this.vslId = this.opts.vslId || "demo-vsl";
    this.sessionId = this.opts.sessionId || generateId("sess");
    this.visitorId = this.opts.visitorId || getOrCreateVisitorId();

    // FUL-107 (IFRAME-7): best-effort postMessage bridge to the embedding host.
    this.embedBridge = new EmbedBridge({
      vslId: this.vslId,
      allowedParentOrigins: this.opts.allowedParentOrigins,
    });

    this.frame = document.createElement("div");
    this.frame.style.setProperty("--fvsl-brand", this.opts.brandColor || "#00685f");
    this.frame.className = "fvsl-frame fvsl-paused";
    this.frame.dataset.orientation = "landscape";

    this.video = document.createElement("video");
    this.video.className = "fvsl-media";
    this.video.src = this.opts.sourceUrl;
    this.video.muted = this.opts.muted;
    this.video.loop = this.opts.loop;
    this.video.playsInline = true;
    this.video.preload = "metadata";
    // Native controls intentionally disabled — we render our own.
    this.video.controls = false;

    this.bigPlay = document.createElement("button");
    this.bigPlay.type = "button";
    this.bigPlay.className = "fvsl-bigplay";
    this.bigPlay.setAttribute("aria-label", "Reproduzir");
    this.bigPlay.innerHTML = ICON_PLAY;

    const center = document.createElement("div");
    center.className = "fvsl-center";
    center.appendChild(this.bigPlay);

    this.playBtn = this.mkBtn("play", ICON_PLAY);
    this.muteBtn = this.mkBtn("mute", this.opts.muted ? ICON_MUTED : ICON_VOL);
    this.fsBtn = this.mkBtn("fullscreen", ICON_FS);

    this.played = document.createElement("div");
    this.played.className = "fvsl-seek-played";
    this.played.style.width = "0%";
    this.buffered = document.createElement("div");
    this.buffered.className = "fvsl-seek-buffered";
    this.buffered.style.width = "0%";
    const track = document.createElement("div");
    track.className = "fvsl-seek-track";
    track.appendChild(this.buffered);
    track.appendChild(this.played);
    this.seek = document.createElement("div");
    this.seek.className = "fvsl-seek";
    this.seek.setAttribute("role", "slider");
    this.seek.setAttribute("aria-label", "Avançar ou voltar");
    this.seek.appendChild(track);

    this.timeLabel = document.createElement("span");
    this.timeLabel.className = "fvsl-time";
    this.timeLabel.textContent = "0:00 / 0:00";

    const ctrl = document.createElement("div");
    ctrl.className = "fvsl-ctrl is-visible";
    ctrl.appendChild(this.playBtn);
    ctrl.appendChild(this.muteBtn);
    ctrl.appendChild(this.seek);
    ctrl.appendChild(this.timeLabel);
    ctrl.appendChild(this.fsBtn);

    this.safeZoneLayer = document.createElement("div");
    this.safeZoneLayer.className = "fvsl-safezones";
    this.safeZoneLayer.setAttribute("aria-hidden", "true");

    // FUL-9: dedicated overlay layer for the CTA (sits above media, below controls).
    this.ctaLayer = document.createElement("div");
    this.ctaLayer.className = "fvsl-cta-layer";

    // FUL-11: dedicated layer for overlay moments (sits above the CTA layer so
    // pause/start/end overlays can cover the frame, below the control bar).
    this.overlayLayer = document.createElement("div");
    this.overlayLayer.className = "fvsl-overlay-layer";

    // FUL-107 (IFRAME-4): resume-prompt bar (continue / start over). Sits above
    // overlays so it is reachable when offered on a return visit.
    this.resumeLayer = document.createElement("div");
    this.resumeLayer.className = "fvsl-resume-layer";

    this.frame.appendChild(this.video);
    this.frame.appendChild(center);
    this.frame.appendChild(this.safeZoneLayer);
    this.frame.appendChild(this.ctaLayer);
    this.frame.appendChild(this.overlayLayer);
    this.frame.appendChild(this.resumeLayer);
    this.frame.appendChild(ctrl);
    this.opts.container.appendChild(this.frame);

    // FUL-107 (IFRAME-4): resume-play persistence + restore/prompt decisioning.
    this.resumeWiring = new ResumeWiring({
      vslId: this.vslId,
      visitorId: this.visitorId,
      sourceUrl: this.opts.sourceUrl,
      config: this.opts.resume ?? DEFAULT_RESUME_CONFIG,
      funnelId: this.opts.funnelId,
      pageId: this.opts.pageId,
      emit: (e) => this.dispatchEvent(e),
    });

    // FUL-9: wire the watched-progress CTA controller when a config is provided.
    if (this.opts.cta) {
      this.ctaController = new CtaController({
        media: this.video,
        layer: this.ctaLayer,
        config: this.opts.cta,
        delayedElements: this.opts.delayedElements,
        headline: this.opts.ctaHeadline,
        subtext: this.opts.ctaSubtext,
        buttonLabel: this.opts.ctaButtonLabel,
        emit: (ctaEvent) => this.dispatchEvent(ctaEvent),
        // IFRAME-3: navigate via the player so it can fall back to postMessage
        // when window.open is blocked by the sandbox.
        onNavigate: (url) => this.openCtaDestination(url),
        onNavigateBlocked: (url) =>
          this.embedBridge.sendNavigate(url, this.opts.cta?.destination.type ?? "url"),
        // IFRAME-10: bridge delayed-element + css_reveal selectors to the host.
        onDelayedReveal: (selector) => this.embedBridge.sendReveal(selector),
        // IFRAME-10: bridge js_event CustomEvents to the host document.
        onCustomEvent: (name, detail) => this.embedBridge.sendCustomEvent(name, detail),
      });
    }

    // FUL-11: wire the overlay-moment renderer when overlays are provided.
    if (this.opts.overlays && this.opts.overlays.length > 0) {
      this.overlayController = new OverlayController({
        layer: this.overlayLayer,
        overlays: this.opts.overlays,
        ctaReveal: this.opts.cta
          ? {
              revealMode: this.opts.cta.revealMode,
              revealAt: this.opts.cta.revealAt,
              persistAfterReveal: this.opts.cta.persistAfterReveal,
            }
          : undefined,
        getLayout: () => this.resolveOverlayLayout(),
        emit: (event) => this.dispatchEvent(event),
        actions: this.buildOverlayActionHandlers(),
      });
    }

    this.applyAspectRatio(this.opts.aspectRatio);
    this.bind();
    // FUL-107 (IFRAME-8): attempt smart autoplay once the element is wired. The
    // actual play() resolves after metadata; safe to call immediately — the
    // browser queues it. Resume restore runs in onLoadedMetadata so the seek
    // targets a known duration.
    this.attemptAutoplay();
  }

  /** Resolve the safe-zone layout overlays position against (mirrors renderSafeZones). */
  private resolveOverlayLayout(): SafeZoneLayout {
    return this.detectedAspectField
      ? resolveSafeZones(this.detectedAspectField)
      : resolveSafeZonesByOrientation(this.lastAppliedOrientation);
  }

  /** Wire overlay button actions back to playback/CTA behavior. */
  private buildOverlayActionHandlers(): OverlayActionHandlers {
    return {
      continue: () => {
        if (this.video.muted) this.video.muted = false;
        void this.video.play();
      },
      restart: () => {
        this.video.currentTime = 0;
        void this.video.play();
      },
      jumpToOffer: () => this.openCtaDestination(),
      openCta: () => {
        this.ctaController?.show({
          currentTimeSec: this.video.currentTime,
          maxWatchedSec: this.maxWatchedSec,
          durationSec: Number.isFinite(this.video.duration) ? this.video.duration : 0,
        });
      },
    };
  }

  /**
   * Open the configured CTA destination (jump_to_offer / open_cta navigation).
   * Returns true when a window/tab was actually opened. When `explicitUrl` is
   * omitted the configured destination URL is used. Callers (CTA controller)
   * use the boolean to decide whether to bridge via postMessage (IFRAME-3).
   */
  private openCtaDestination(explicitUrl?: string): boolean {
    const dest = this.opts.cta?.destination;
    if (!dest) return false;
    const isNavigate = ["url", "funnel_checkout", "form", "calendar"].includes(dest.type);
    if (!isNavigate) return false;
    const url = explicitUrl ?? dest.value;
    if (!url) return false;
    try {
      const handle = window.open(url, "_blank", "noopener,noreferrer");
      if (handle) return true;
    } catch {
      /* popup blocked — fall through to signal failure */
    }
    // IFRAME-3: window.open blocked (sandbox without allow-popups) — ask the
    // host to navigate on the player's behalf.
    this.embedBridge.sendNavigate(url, dest.type);
    return false;
  }

  private mkBtn(name: string, icon: string): HTMLButtonElement {
    const b = document.createElement("button");
    b.type = "button";
    b.className = `fvsl-btn fvsl-btn-${name}`;
    b.setAttribute("aria-label", name);
    b.innerHTML = icon;
    return b;
  }

  private bind(): void {
    this.video.addEventListener("loadedmetadata", this.onLoadedMetadata);
    this.video.addEventListener("play", this.onPlay);
    this.video.addEventListener("pause", this.onPause);
    this.video.addEventListener("ended", this.onEnded);
    this.video.addEventListener("timeupdate", this.onTimeUpdate);
    this.video.addEventListener("seeking", this.onSeeking);
    this.video.addEventListener("progress", this.onProgress);
    this.video.addEventListener("volumechange", this.onVolumeChange);

    this.playBtn.addEventListener("click", this.togglePlay);
    this.bigPlay.addEventListener("click", this.togglePlay);
    this.muteBtn.addEventListener("click", this.toggleMute);
    this.fsBtn.addEventListener("click", this.toggleFullscreen);

    this.seek.addEventListener("pointerdown", this.onSeekStart);
    this.frame.addEventListener("pointermove", this.scheduleHide);
    this.frame.addEventListener("pointerleave", this.hideControlsSoon);

    document.addEventListener("fullscreenchange", this.onFullscreenChange);
  }

  /** Resolve/apply aspect ratio from an input value (no media dimensions yet). */
  private applyAspectRatio(input: AspectRatioInput, dims?: { w: number; h: number }): void {
    let cssValue = "16 / 9";
    let orientation: Orientation = "landscape";
    let label: StandardAspectRatio | "custom" | "auto" = input;

    if (input === "auto") {
      if (dims && dims.w > 0 && dims.h > 0) {
        const det = classifyAspectRatio(dims.w, dims.h);
        cssValue = `${dims.w} / ${dims.h}`;
        orientation = det.orientation;
        label = det.label;
      } else {
        // Default until metadata arrives.
        cssValue = "16 / 9";
        orientation = "landscape";
        label = "auto";
      }
    } else if (input === "custom") {
      // Prefer an explicit customRatio (Phase 0 contract: VslProject.customRatio {w,h});
      // fall back to detected media dimensions, else keep 16:9 placeholder.
      const cw = this.opts.customRatio?.w ?? dims?.w ?? 0;
      const ch = this.opts.customRatio?.h ?? dims?.h ?? 0;
      if (cw > 0 && ch > 0) {
        cssValue = `${cw} / ${ch}`;
        orientation = resolveOrientation(cw, ch);
      } else {
        cssValue = "16 / 9";
      }
    } else {
      const [w, h] = input.split(":").map(Number);
      cssValue = `${w} / ${h}`;
      orientation = resolveOrientation(w, h);
    }

    this.frame.style.aspectRatio = cssValue;
    this.frame.dataset.orientation = orientation;
    this.frame.dataset.aspectLabel = label;
    this.lastAppliedOrientation = orientation;
    this.opts.onOrientationChange?.(orientation);
    this.renderSafeZones();
  }

  /**
   * Render the overlay/CTA safe-zone grid into the safe-zone layer. When
   * `showSafeZones` is off the layer is emptied and hidden. Layout is resolved
   * from detected media dimensions when available, otherwise from the last
   * applied orientation (used for the placeholder frame before metadata loads).
   */
  private renderSafeZones(): void {
    const layer = this.safeZoneLayer;
    layer.innerHTML = "";
    if (!this.opts.showSafeZones) {
      layer.classList.remove("is-on");
      delete layer.dataset.label;
      // Overlays still reposition on aspect change even when the debug grid is off.
      this.overlayController?.relayout();
      return;
    }
    layer.classList.add("is-on");
    const layout = this.detectedAspectField
      ? resolveSafeZones(this.detectedAspectField)
      : resolveSafeZonesByOrientation(this.lastAppliedOrientation);
    layer.dataset.label = layout.label;
    for (const position of OVERLAY_POSITIONS) {
      const region: SafeZoneRegion = layout.regions[position];
      const cell = document.createElement("div");
      cell.className = "fvsl-safezone";
      cell.dataset.position = position;
      cell.style.left = `${(region.x * 100).toFixed(3)}%`;
      cell.style.top = `${(region.y * 100).toFixed(3)}%`;
      cell.style.width = `${(region.width * 100).toFixed(3)}%`;
      cell.style.height = `${(region.height * 100).toFixed(3)}%`;
      const tag = document.createElement("span");
      tag.className = "fvsl-safezone-tag";
      tag.textContent = position;
      cell.appendChild(tag);
      layer.appendChild(cell);
    }
    this.overlayController?.relayout();
  }

  private onLoadedMetadata = (): void => {
    const w = this.video.videoWidth;
    const h = this.video.videoHeight;
    if (this.opts.aspectRatio === "auto" || this.opts.aspectRatio === "custom") {
      if (w > 0 && h > 0) {
        this.applyAspectRatio(this.opts.aspectRatio, { w, h });
      }
    }
    if (w > 0 && h > 0) {
      this.detectedAspectField = classifyAspectRatio(w, h);
      this.opts.onAspectDetected?.(this.detectedAspectField);
      this.renderSafeZones();
    }
    this.overlayController?.relayout();
    // FUL-107 (IFRAME-4): restore saved resume state BEFORE the first timeupdate
    // checkpoint runs — otherwise the checkpoint at position 0 would overwrite
    // the seeded/returning-visit state and the prompt would never fire.
    this.restoreResume();
    this.onTimeUpdate();
  };

  private onPlay = (): void => {
    this.frame.classList.add("fvsl-playing");
    this.frame.classList.remove("fvsl-paused");
    this.playBtn.innerHTML = ICON_PAUSE;
    this.playBtn.setAttribute("aria-label", "Pausar");
    this.hideControlsSoon();
    this.dispatchEvent(this.buildSimpleEvent("play"));
    this.overlayController?.notify({ hasStarted: true, isPaused: false });
  };

  private onPause = (): void => {
    this.frame.classList.remove("fvsl-playing");
    this.frame.classList.add("fvsl-paused");
    this.playBtn.innerHTML = ICON_PLAY;
    this.playBtn.setAttribute("aria-label", "Reproduzir");
    this.showControls();
    // Only treat as a user pause (not the ended transition) for analytics.
    if (this.video.ended) return;
    this.dispatchEvent(this.buildSimpleEvent("pause"));
    this.ctaController?.notifyPause();
    this.overlayController?.notify({ isPaused: true });
    // FUL-107 (IFRAME-4): persist a checkpoint on pause so a return visit resumes.
    this.checkpointResume();
  };

  private onEnded = (): void => {
    // onPause already flipped the UI classes; emit completion + final CTA check.
    const { cur, dur } = this.progressClock();
    if (dur > 0) this.maxWatchedSec = Math.max(this.maxWatchedSec, dur);
    this.dispatchEvent(this.buildSimpleEvent("completed"));
    this.ctaController?.notifyComplete();
    this.ctaController?.update({ currentTimeSec: cur, maxWatchedSec: this.maxWatchedSec, durationSec: dur });
    this.overlayController?.notify({ completed: true, isPaused: false });
    // FUL-107 (IFRAME-4): completed — clear saved position so a replay starts fresh.
    this.resumeWiring?.clear();
  };

  /** On a user-initiated seek, reset the natural-step baseline so the jump is
   * not credited to maxWatched (anti-gaming against forward-skipping the CTA). */
  private onSeeking = (): void => {
    this.lastCurrentTimeSec = this.video.currentTime;
    this.checkpointResume();
  };

  private onTimeUpdate = (): void => {
    const cur = this.video.currentTime;
    const dur = this.video.duration;
    const realRatio = dur > 0 ? cur / dur : 0;
    this.played.style.width = `${toVisualProgress(realRatio, this.opts.progressBar) * 100}%`;
    this.timeLabel.textContent = `${fmtTime(cur)} / ${fmtTime(dur)}`;

    // FUL-9: advance watched high-water mark on natural playback only.
    if (!this.video.seeking) {
      this.maxWatchedSec = advanceMaxWatched(this.maxWatchedSec, cur, this.lastCurrentTimeSec);
    }
    this.lastCurrentTimeSec = cur;

    const progress: WatchProgress = {
      currentTimeSec: cur,
      maxWatchedSec: this.maxWatchedSec,
      durationSec: Number.isFinite(dur) ? dur : 0,
    };

    this.ctaController?.update(progress);
    this.overlayController?.update(progress);
    this.emitProgressMaybe(progress);
    this.emitMilestonesMaybe(progress);
    this.checkpointResume();
  };

  private onProgress = (): void => {
    const dur = this.video.duration;
    if (!dur || dur <= 0) return;
    let end = 0;
    try {
      const ranges = this.video.buffered;
      if (ranges.length > 0) end = ranges.end(ranges.length - 1);
    } catch {
      end = 0;
    }
    this.buffered.style.width = `${toVisualProgress(end / dur, this.opts.progressBar) * 100}%`;
  };

  private onVolumeChange = (): void => {
    this.muteBtn.innerHTML = this.video.muted || this.video.volume === 0 ? ICON_MUTED : ICON_VOL;
    this.muteBtn.setAttribute("aria-label", this.video.muted ? "Ativar som" : "Silenciar");
    this.overlayController?.notify({ isMuted: this.video.muted || this.video.volume === 0 });
  };

  togglePlay = (): void => {
    if (this.video.paused) {
      void this.video.play();
    } else {
      this.video.pause();
    }
  };

  toggleMute = (): void => {
    this.video.muted = !this.video.muted;
    if (!this.video.muted && this.video.volume === 0) this.video.volume = 1;
  };

  toggleFullscreen = (): void => {
    const isFs = document.fullscreenElement === this.frame;
    if (!isFs) {
      // IFRAME-9: requestFullscreen is rejected silently inside an iframe that
      // lacks allowfullscreen. Detect capability, surface feedback to the user,
      // and ask the host to fullscreen the embed iframe via postMessage.
      const canFs = typeof this.frame.requestFullscreen === "function";
      if (!canFs) {
        this.onFullscreenUnavailable();
        return;
      }
      this.frame
        .requestFullscreen()
        .then(() => {
          this.fullscreenBlocked = false;
        })
        .catch(() => {
          this.onFullscreenUnavailable();
        });
    } else {
      void document.exitFullscreen?.().catch(() => {});
      this.embedBridge.sendFullscreen("exit");
    }
  };

  /**
   * FUL-107 (IFRAME-9): fullscreen could not engage (no API or rejected, e.g.
   * iframe without allowfullscreen). Show a short toast and ask the host to
   * fullscreen the embed iframe on the viewer's behalf.
   */
  private onFullscreenUnavailable(): void {
    if (this.fullscreenBlocked) return;
    this.fullscreenBlocked = true;
    this.embedBridge.sendFullscreen("enter");
    this.showToast("A tela cheia não está disponível nesta incorporação");
  }

  /** Short, non-blocking status toast (fullscreen / capability feedback). */
  private showToast(text: string): void {
    const toast = document.createElement("div");
    toast.className = "fvsl-toast";
    toast.setAttribute("role", "status");
    toast.textContent = text;
    this.frame.appendChild(toast);
    // Animate in on next frame.
    requestAnimationFrame(() => toast.classList.add("is-visible"));
    setTimeout(() => {
      toast.classList.remove("is-visible");
      setTimeout(() => toast.remove(), 250);
    }, 2600);
  }

  private onFullscreenChange = (): void => {
    const isFs = document.fullscreenElement === this.frame;
    this.frame.classList.toggle("fvsl-fullscreen", isFs);
    // In fullscreen, fill the screen regardless of orientation caps.
    if (isFs) this.frame.style.maxWidth = "none";
    else this.frame.style.maxWidth = "";
  };

  private seekToClientX = (clientX: number): void => {
    const rect = this.seek.getBoundingClientRect();
    if (rect.width <= 0) return;
    const visualRatio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const realRatio = fromVisualProgress(visualRatio, this.opts.progressBar);
    const dur = this.video.duration;
    if (Number.isFinite(dur) && dur > 0) {
      this.video.currentTime = realRatio * dur;
      this.played.style.width = `${visualRatio * 100}%`;
    }
  };

  private onSeekStart = (ev: PointerEvent): void => {
    ev.preventDefault();
    this.seek.setPointerCapture(ev.pointerId);
    this.seekToClientX(ev.clientX);
    const move = (e: PointerEvent) => this.seekToClientX(e.clientX);
    const up = (e: PointerEvent) => {
      this.seekToClientX(e.clientX);
      this.seek.releasePointerCapture(e.pointerId);
      this.seek.removeEventListener("pointermove", move);
      this.seek.removeEventListener("pointerup", up);
      this.seek.removeEventListener("pointercancel", up);
    };
    this.seek.addEventListener("pointermove", move);
    this.seek.addEventListener("pointerup", up);
    this.seek.addEventListener("pointercancel", up);
  };

  private showControls(): void {
    const ctrl = this.frame.querySelector(".fvsl-ctrl") as HTMLElement | null;
    ctrl?.classList.add("is-visible");
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  private hideControlsSoon = (): void => {
    this.showControls();
    if (this.video.paused) return;
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      const ctrl = this.frame.querySelector(".fvsl-ctrl") as HTMLElement | null;
      ctrl?.classList.remove("is-visible");
    }, 2400);
  };

  private scheduleHide = (): void => {
    this.hideControlsSoon();
  };

  /**
   * FUL-107 (IFRAME-8): smart autoplay. Attempts playback using the configured
   * muted state; if unmuted autoplay is blocked by the browser, falls back to
   * muted autoplay + "tap for sound" prompt (the VTurb/Vidalytics default). On
   * success the autoplay overlay is surfaced via the overlay lifecycle signal.
   */
  private attemptAutoplay(): void {
    const cfg = this.opts.autoplay;
    if (!cfg || cfg.enabled === false) return;
    const wantMuted = cfg.muted;
    this.video.muted = wantMuted;
    const started = (muted: boolean): void => {
      this.autoplayEngaged = true;
      this.dispatchEvent(this.buildSimpleEvent("autoplay_impression"));
      // The autoplay overlay + tap-to-unmute prompt are driven by isMuted.
      this.overlayController?.notify({ isMuted: muted, hasStarted: true });
    };
    const failMuted = (): void => {
      // Even muted autoplay was blocked (very restrictive browser) — leave paused;
      // the start overlay remains as the manual-play affordance.
    };
    const tryPlay = (muted: boolean): void => {
      this.video.muted = muted;
      const p = this.video.play();
      if (!p || typeof p.then !== "function") {
        // Synchronous (older engines) — assume it started.
        started(muted);
        return;
      }
      p.then(() => started(muted)).catch(() => {
        if (!muted) {
          // Unmuted autoplay blocked — retry once muted (smart fallback).
          tryPlay(true);
        } else {
          failMuted();
        }
      });
    };
    tryPlay(wantMuted);
  }

  /**
   * FUL-107 (IFRAME-4): restore saved resume state once metadata/duration is
   * known. For mode "ask", surface a resume prompt; for "always_resume", seek
   * silently. Skipped while muted autoplay is actively engaging playback.
   */
  private restoreResume(): void {
    if (!this.resumeWiring) return;
    const dur = Number.isFinite(this.video.duration) ? this.video.duration : 0;
    const result = this.resumeWiring.restore(this.video, dur);
    if (result.shouldPrompt) this.showResumePrompt(result.promptFromSec);
  }

  /** Persist a resume checkpoint from the current playback clock. */
  private checkpointResume(): void {
    if (!this.resumeWiring) return;
    // While a resume prompt is pending, do NOT overwrite the saved position
    // with the live (autoplay) clock — otherwise acceptResume would seek to the
    // post-prompt position instead of the return-visit position.
    if (this.resumePromptVisible) return;
    const dur = Number.isFinite(this.video.duration) ? this.video.duration : 0;
    this.resumeWiring.checkpoint({
      positionSec: this.video.currentTime,
      maxWatchedSec: this.maxWatchedSec,
      durationSec: dur,
      ctaShown: this.ctaController?.isVisible,
    });
  }

  /**
   * FUL-107 (IFRAME-4): render the resume-prompt bar ("Continuar de Xs" /
   * "Reiniciar"). Shown only for ResumeConfig mode "ask" on a return visit.
   */
  private showResumePrompt(fromSec: number): void {
    if (this.resumeLayer.querySelector(".fvsl-resume")) return;
    const card = document.createElement("div");
    card.className = "fvsl-resume";
    card.setAttribute("role", "dialog");
    card.setAttribute("aria-label", "Retomar reprodução");

    const text = document.createElement("span");
    text.className = "fvsl-resume-text";
    text.textContent = `Continuar de ${fmtTime(fromSec)}?`;

    const resumeBtn = document.createElement("button");
    resumeBtn.type = "button";
    resumeBtn.className = "fvsl-resume-btn";
    resumeBtn.textContent = "Continuar";

    const restartBtn = document.createElement("button");
    restartBtn.type = "button";
    restartBtn.className = "fvsl-resume-btn fvsl-resume-btn--ghost";
    restartBtn.textContent = "Reiniciar";

    resumeBtn.addEventListener("click", () => {
      this.resumeWiring?.acceptResume(this.video);
      card.remove();
    });
    restartBtn.addEventListener("click", () => {
      this.resumeWiring?.declineResume();
      this.resumeWiring?.clear();
      card.remove();
    });

    card.appendChild(text);
    card.appendChild(resumeBtn);
    card.appendChild(restartBtn);
    this.resumeLayer.appendChild(card);
    // Animate in on the next frame.
    requestAnimationFrame(() => card.classList.add("is-visible"));
  }

  /** Public API helpers (kept minimal for the prototype). */
  play(): Promise<void> {
    return this.video.play();
  }
  pause(): void {
    this.video.pause();
  }

  /**
   * FUL-107 (IFRAME-3): open the configured offer/CTA destination. Returns true
   * when a window/tab opened; when blocked (sandbox without allow-popups) the
   * player asks the host to navigate via postMessage. Public so install code /
   * host bridges can trigger the offer on demand.
   */
  openOffer(): boolean {
    return this.openCtaDestination();
  }

  /**
   * FUL-107 (IFRAME-10): ask the host to reveal a page element by selector. The
   * iframe cannot reach cross-origin host DOM, so this bridges the request;
   * install code on the Full Funnel host listens and reveals the matching nodes.
   */
  revealHostElement(selector: string): void {
    this.embedBridge.sendReveal(selector);
  }

  /** FUL-107 (IFRAME-4): accept an offered resume prompt (install-API hook). */
  acceptResume(): void {
    this.resumeWiring?.acceptResume(this.video);
    const card = this.resumeLayer.querySelector(".fvsl-resume");
    card?.remove();
  }

  /** FUL-107 (IFRAME-4): decline an offered resume prompt (install-API hook). */
  declineResume(): void {
    this.resumeWiring?.declineResume();
    this.resumeWiring?.clear();
    const card = this.resumeLayer.querySelector(".fvsl-resume");
    card?.remove();
  }

  /** FUL-107 (IFRAME-8): whether muted autoplay engaged on load. */
  get autoplayStarted(): boolean {
    return this.autoplayEngaged;
  }

  /** FUL-107 (IFRAME-4): whether a resume prompt is currently visible. */
  get resumePromptVisible(): boolean {
    return !!this.resumeLayer.querySelector(".fvsl-resume");
  }

  /**
   * Switch the aspect-ratio mode at runtime (editor / QA). When `input` is
   * "custom" an optional `customRatio` pins the frame; otherwise media metadata
   * (when available) is used to detect the ratio. Re-renders the safe-zone grid.
   */
  setAspectRatio(input: AspectRatioInput, customRatio?: { w: number; h: number }): void {
    if (customRatio) this.opts.customRatio = customRatio;
    this.opts.aspectRatio = input;
    this.applyAspectRatio(input);
  }

  /** Toggle the overlay/CTA safe-zone grid overlay (editor / debug aid). */
  setShowSafeZones(show: boolean): void {
    this.opts.showSafeZones = show;
    this.renderSafeZones();
  }

  get detectedAspect(): DetectedAspect | null {
    return this.detectedAspectField;
  }

  /** FUL-9: greatest contiguous watched position, seconds. */
  get maxWatchedSeconds(): number {
    return this.maxWatchedSec;
  }

  /** FUL-9: whether the CTA overlay is currently revealed. */
  get ctaVisible(): boolean {
    return this.ctaController?.isVisible ?? false;
  }

  /** FUL-11: replace the rendered overlay set (editor live-preview path). */
  setOverlays(overlays: Overlay[]): void {
    this.opts.overlays = overlays;
    if (overlays.length === 0) {
      this.overlayController?.destroy();
      this.overlayController = null;
      return;
    }
    if (this.overlayController) {
      this.overlayController.setOverlays(overlays);
    } else {
      this.overlayController = new OverlayController({
        layer: this.overlayLayer,
        overlays,
        ctaReveal: this.opts.cta
          ? {
              revealMode: this.opts.cta.revealMode,
              revealAt: this.opts.cta.revealAt,
              persistAfterReveal: this.opts.cta.persistAfterReveal,
            }
          : undefined,
        getLayout: () => this.resolveOverlayLayout(),
        emit: (event) => this.dispatchEvent(event),
        actions: this.buildOverlayActionHandlers(),
      });
    }
  }

  /** FUL-11: whether a given overlay instance is currently visible. */
  isOverlayVisible(overlayId: string): boolean {
    return this.overlayController?.isVisible(overlayId) ?? false;
  }

  private progressClock(): { cur: number; dur: number; pct: number } {
    const cur = this.video.currentTime;
    const dur = this.video.duration;
    const pct = dur > 0 ? (cur / dur) * 100 : 0;
    return { cur, dur: Number.isFinite(dur) ? dur : 0, pct };
  }

  /** Build a simple (non-progress) player event with identity + seq filled in. */
  private buildSimpleEvent(
    name: "impression" | "autoplay_impression" | "play" | "pause" | "completed",
  ): PlayerEvent {
    const { cur, dur } = this.progressClock();
    return {
      seq: ++this.eventSeq,
      name,
      clientTs: new Date().toISOString(),
      vslId: this.vslId,
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      positionSec: cur,
      durationSec: dur,
    };
  }

  /** Fill identity/seq on an event built by the CTA controller and dispatch it. */
  private dispatchEvent(event: PlayerEvent): void {
    const enriched: PlayerEvent = {
      ...event,
      seq: ++this.eventSeq,
      clientTs: new Date().toISOString(),
      vslId: this.vslId,
      sessionId: this.sessionId,
      visitorId: this.visitorId,
    };
    this.opts.onEvent?.(enriched);
    // FUL-107 (IFRAME-7): mirror every normalized event to the embedding host so
    // analytics/CTA/resume signals are not trapped inside the iframe.
    if (this.opts.emitAnalyticsToParent !== false) this.embedBridge.sendEvent(enriched);
    // FUL-107 (IFRAME-4): once the CTA is revealed, persist the flag so a
    // returning visitor re-sees it without re-watching to the threshold.
    if (enriched.name === "cta_shown") {
      this.resumeWiring?.markCtaShown(enriched.positionSec ?? 0, this.maxWatchedSec);
    }
  }

  /** Throttled `progress` emission (FUL-5: at most every 1s AND every 5%). */
  private emitProgressMaybe(progress: WatchProgress): void {
    if (progress.durationSec <= 0) return;
    const now = Date.now();
    const pct = progress.maxWatchedSec / progress.durationSec * 100;
    const sinceMs = now - this.lastProgressEmitMs;
    const pctStep = Math.abs(pct - this.lastProgressEmitPct);
    const due = sinceMs >= PROGRESS_MIN_INTERVAL_MS && pctStep >= PROGRESS_MIN_PCT_STEP;
    if (!due) return;
    this.lastProgressEmitMs = now;
    this.lastProgressEmitPct = pct;
    this.dispatchEvent({
      seq: 0,
      name: "progress",
      clientTs: "",
      vslId: "",
      sessionId: "",
      visitorId: "",
      positionSec: progress.currentTimeSec,
      maxWatchedSec: progress.maxWatchedSec,
      durationSec: progress.durationSec,
      pct: Math.round(pct * 100) / 100,
    });
  }

  /** Emit each milestone (25/50/75/100) exactly once, based on maxWatched. */
  private emitMilestonesMaybe(progress: WatchProgress): void {
    if (progress.durationSec <= 0) return;
    const watchedPct = (progress.maxWatchedSec / progress.durationSec) * 100;
    for (const m of MILESTONES) {
      if (watchedPct >= m && !this.milestonesHit.has(m)) {
        this.milestonesHit.add(m);
        this.dispatchEvent({
          seq: 0,
          name: "milestone",
          clientTs: "",
          vslId: "",
          sessionId: "",
          visitorId: "",
          milestone: m as 25 | 50 | 75 | 100,
          positionSec: progress.maxWatchedSec,
          durationSec: progress.durationSec,
        });
      }
    }
  }

  destroy(): void {
    // FUL-107 (IFRAME-4): persist the final position before teardown so a quick
    // navigation away still records the last watched checkpoint.
    this.checkpointResume();
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.ctaController?.destroy();
    this.ctaController = null;
    this.overlayController?.destroy();
    this.overlayController = null;
    this.video.removeEventListener("loadedmetadata", this.onLoadedMetadata);
    this.video.removeEventListener("play", this.onPlay);
    this.video.removeEventListener("pause", this.onPause);
    this.video.removeEventListener("ended", this.onEnded);
    this.video.removeEventListener("timeupdate", this.onTimeUpdate);
    this.video.removeEventListener("seeking", this.onSeeking);
    this.video.removeEventListener("progress", this.onProgress);
    this.video.removeEventListener("volumechange", this.onVolumeChange);
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    this.frame.remove();
  }
}
